import {
    ChangeDetectorRef,
    Component,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Store } from '@ngxs/store';
import { PostHogService } from '@services/common/post-hog.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { FeatureService } from '@services/features/feature.service';
import { QuestionnaireConfigHttpService } from '@services/datasources/questionnaireConfig-http.service';
import { UtilsService } from '@services/common/utils.service';
import { QuestionnaireAction, QuestionnaireState } from '@state/questionnaire.state';
import { EnumEntryDto } from 'app/@types/dto/enum-entry-dto.model';
import { QuestionDto } from 'app/@types/dto/question-dto.model';
import { QuestionInstanceDto } from 'app/@types/dto/question-instance-dto.model';
import { Subject, takeUntil } from 'rxjs';

export interface QuestionInstanceInputEntry {
    instanceId: string | undefined;
    instance: QuestionInstanceDto;
    editForm: FormGroup;
    value1Control: UntypedFormControl;
    value2Control: UntypedFormControl;
    value3Control: UntypedFormControl;
    unitControl: UntypedFormControl;
    value1ControlInitValue: string;
    value2ControlInitValue: string;
    value3ControlInitValue: string;
    unitControlInitValue: string;
    unitFromSelectedDropdown?: string;
    isDisabledLike: boolean;
    input1UpdateFailed?: boolean;
    input2UpdateFailed?: boolean;
    input3UpdateFailed?: boolean;
    hidden?: boolean;
    possibleUnits?: string[];
    isPrefilled?: boolean;
}

export interface inputUpdateMessage {
    inputEntry: QuestionInstanceInputEntry;
    questionField: number;
}

export type EnumEntryTranslatedDTO = EnumEntryDto & { translatedLabel?: string };

@Component({
    selector: 'app-question',
    templateUrl: './question.component.html',
    styleUrls: ['./question.component.scss'],
    standalone: false,
})
export class QuestionComponent implements OnInit, OnChanges, OnDestroy {
    @Input() question!: QuestionDto;
    @Input() domainPerCode!: { [key: string]: EnumEntryDto[] };
    @Input() orgId!: string;
    @Input() questionnaireId!: string;
    @Input() locale?: string;
    @Input() pageQuestions!: QuestionDto[];
    @Input() parentQuestionInstance?: QuestionInstanceInputEntry;
    @Input() currentEntityFte: number = 0;

    @ViewChild('subQuestionComponent') childComponent?: QuestionComponent;

    public _fb: UntypedFormBuilder;

    private ngUnsubscribe = new Subject<void>();
    protected isLocked = false;
    public isInit: boolean = false;
    public questionInstancesInputEntries: QuestionInstanceInputEntry[] = [];
    public hasNoNewEntry: boolean = false;
    public maxInstances: number = 0;
    public domain1: EnumEntryTranslatedDTO[] = [];
    public domain1Map: Map<string, EnumEntryTranslatedDTO> = new Map();
    public subQuestion?: QuestionDto;
    public nonHiddenEntries: QuestionInstanceInputEntry[] = [];
    public activeIndex: number = -1;
    // The accordion's own tokens for an entry list drawn as cards (see `.entry-card`): no rule under
    // each panel, a tighter header, the form inset in its card.
    readonly entryCardTokens = {
        panel: { borderWidth: '0' },
        header: {
            padding: '0.75rem 1rem',
            color: '{text.color}',
            hoverColor: '{text.color}',
            hoverBackground: '{surface.50}',
            activeBackground: '{surface.50}',
            activeHoverBackground: '{surface.50}',
        },
        content: { padding: '1.25rem 1rem' },
    };
    tooltipContent: string = '';

    // Read-only (Viewer role): mirrors the backend write block by disabling/hiding edit controls.
    public readOnly = false;

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store,
        private postHogService: PostHogService,
        public translateService: CustomTranslateService,
        private cdr: ChangeDetectorRef,
        private questionnaireConfigService: QuestionnaireConfigHttpService,
        private ngZone: NgZone,
        private readonly utilsService: UtilsService,
        private readonly featureService: FeatureService,
    ) {
        this._fb = fb;
    }

    /**
     * The field a question's title labels: its first input, when that is a text or number field
     * (a select or a radio group gets its name from `ariaLabel` instead). See question-instance.
     */
    titleFieldId(entry: QuestionInstanceInputEntry, qIndex: number): string | null {
        const id = `${entry.instance.questionCode}_${qIndex}`;
        switch (this.question.value.value1Type) {
            case 'text':
                return id;
            case 'number':
                return `${id}-input`;
            default:
                return null;
        }
    }

    ngOnInit(): void {
        this.featureService.isReadOnly$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((readOnly) => (this.readOnly = readOnly));

        if (this.question.value.domainValue1) {
            this.maxInstances = Object.keys(this.domainPerCode[this.question.value.domainValue1]).length;
            this.domain1Map = this.domainPerCode[this.question.value.domainValue1].reduce((map, dto) => {
                map.set(dto.value, dto);
                return map;
            }, new Map<string, EnumEntryDto>());
        }

        this.loadQuestionInstancesFromStore();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Parent re-uses the same QuestionComponent instance across scans when the questionnaire
        // config matches (same *ngFor item). When the active questionnaire changes (e.g. user
        // navigates from scan A → summary → scan B), only [questionnaireId] flips — selectOnce in
        // ngOnInit has already fired & unsubscribed, so without this hook the displayed values
        // stay on the previous scan's instances. Re-pull fresh instances on each questionnaire
        // switch so the inputs reflect the now-active scan.
        const qIdChange = changes['questionnaireId'];
        if (qIdChange && !qIdChange.firstChange && qIdChange.currentValue !== qIdChange.previousValue) {
            this.questionInstancesInputEntries = [];
            this.nonHiddenEntries = [];
            this.isInit = false;
            this.loadQuestionInstancesFromStore();
        }
    }

    private loadQuestionInstancesFromStore(): void {
        this.store
            .selectOnce(QuestionnaireState.getQuestionInstances)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((map) => {
                let questionInstances = map?.get(this.question.questionCode) ?? [];
                if (this.question.option?.isSubQuestion) {
                    questionInstances = questionInstances.filter(
                        (qI) => qI.relatedQuestionInstanceId === this.parentQuestionInstance?.instanceId,
                    );
                }

                this.domain1 = Array.from(this.domain1Map.values()).map((elt) => {
                    return {
                        ...elt,
                        translatedLabel: this.translateService.instant(`enum.${elt.domain}.${elt.value}`),
                    };
                });

                if (!this.isInit) {
                    if (questionInstances && questionInstances?.length !== 0) {
                        let tempMap: Map<string, EnumEntryDto> = new Map();

                        if (this.question.value.value1Type === 'enum') {
                            tempMap = new Map([...this.domain1Map]);
                        }

                        for (const questionInstance of questionInstances) {
                            this.AddQuestionInstanceInputEntry(questionInstance, undefined);

                            if (this.question.value.value1Type === 'enum' && questionInstance.value.value1) {
                                tempMap.delete(questionInstance.value.value1);
                            }
                        }

                        if (this.question.value.value1Type === 'enum') {
                            this.domain1 = Array.from(tempMap.values()).map((elt) => {
                                return {
                                    ...elt,
                                    translatedLabel: this.translateService.instant(`enum.${elt.domain}.${elt.value}`),
                                };
                            });
                        }
                    }
                    this.preloadTooltipContent();
                    this.sortOptions();
                    this.handleEmptyEntriesSetup();
                    this.handleHiddenEntries();
                    this.hasNoNewEntry = false;
                    this.isInit = true;
                    this.cdr.detectChanges();
                }
            });

        this.translateService.onLangChange.subscribe((_) => {
            this.preloadTooltipContent();
            this.sortOptions();
        });

        this.store
            .select(QuestionnaireState.getIsLocked)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((locked) => {
                this.isLocked = locked;
                this.questionInstancesInputEntries.forEach((e) => (e.isDisabledLike = locked));
                this.cdr.detectChanges();
            });

        if (this.question.option?.subQuestionCode) {
            this.subQuestion = this.pageQuestions.find(
                (question) => question.questionCode === this.question.option?.subQuestionCode,
            );
        }
    }

    private sortOptions(): void {
        this.domain1.forEach((elt) => {
            elt.translatedLabel = this.translateService.instant(`enum.${elt.domain}.${elt.value}`);
        });

        this.domain1.sort((a, b) => {
            return (a.translatedLabel ?? '').toLowerCase().localeCompare((b.translatedLabel ?? '').toLowerCase());
        });
    }

    private handleEmptyEntriesSetup(): void {
        const itCount: number =
            (this.question.isDynamic ? this.maxInstances : 1) -
            this.questionInstancesInputEntries.filter((i) => i.instanceId).length;
        for (let i = 0; i < itCount; i++) {
            this.AddQuestionInstanceInputEntry();
        }
    }

    public sortEntries(): void {
        this.questionInstancesInputEntries.sort((a, b) =>
            a.instanceId && b.instanceId ? 0 : a.instanceId && !b.instanceId ? -1 : 1,
        );
    }

    public handleHiddenEntries(): void {
        // An entry list in an accordion (the fleet) shows its saved entries only: a new one starts from
        // the add button below the list. Inline lists keep their first blank row on screen.
        const hideEveryBlankEntry = this.isAccordionList;
        for (let i = 0; i < this.questionInstancesInputEntries.length; i++) {
            if (!this.questionInstancesInputEntries[i].instanceId) {
                this.questionInstancesInputEntries[i].hidden = hideEveryBlankEntry || i > 0;
            }
        }
        this.nonHiddenEntries = this.questionInstancesInputEntries.filter((entry) => !entry.hidden);
    }

    get isAccordionList(): boolean {
        return !!this.question.option?.subQuestionCode;
    }

    private updateInputEntry(questionInstance: QuestionInstanceDto, input: QuestionInstanceInputEntry): void {
        const value1Control = this._fb.control(null);
        const value2Control = this._fb.control(null);
        const value3Control = this._fb.control(null);
        const unitControl = this._fb.control(null);

        const editForm = this._fb.group({
            value1Control: value1Control,
            value2Control: value2Control,
            value3Control: value3Control,
            unitControl: unitControl,
        });

        value1Control.setValue(questionInstance.value.value1 ?? null);
        value2Control.setValue(questionInstance.value.value2 ?? null);
        value3Control.setValue(questionInstance.value.value3 ?? null);
        unitControl.setValue(questionInstance.unit ?? null);

        input.value1ControlInitValue = questionInstance.value.value1 ?? '';
        input.value2ControlInitValue = questionInstance.value.value2 ?? '';
        input.value3ControlInitValue = questionInstance.value.value3 ?? '';
        input.unitControlInitValue = questionInstance.unit ?? '';
        input.value1Control = value1Control;
        input.value2Control = value2Control;
        input.value3Control = value3Control;
        input.unitControl = unitControl;
        input.editForm = editForm;
        input.instance = questionInstance;
        input.instanceId = questionInstance.id;
        if (questionInstance.value.value1) {
            const enumEntry = this.domain1Map.get(questionInstance.value.value1);
            input.possibleUnits = enumEntry?.possibleUnits;
        }
        input.editForm.markAsPristine();
        this.cdr.detectChanges();
    }

    public hasNotEmptyInput(): boolean {
        return this.questionInstancesInputEntries.filter((i) => i.instanceId === undefined && !i.hidden).length === 0;
    }

    private resetInputEntry(input: QuestionInstanceInputEntry): void {
        const value1Control = this._fb.control(null);
        const value2Control = this._fb.control(null);
        const value3Control = this._fb.control(null);
        const unitControl = this._fb.control(
            this.question.value.unitValue1 ?? this.question.value.unitValue2 ?? this.question.value.unitValue3 ?? null,
        );

        const editForm = this._fb.group({
            value1Control: value1Control,
            value2Control: value2Control,
            value3Control: value3Control,
            unitControl: unitControl,
        });
        input.unitFromSelectedDropdown = undefined;
        input.value1ControlInitValue = '';
        input.value2ControlInitValue = '';
        input.value3ControlInitValue = '';
        input.unitControlInitValue = '';
        input.value1Control = value1Control;
        input.value2Control = value2Control;
        input.value3Control = value3Control;
        input.unitControl = unitControl;
        input.editForm = editForm;
        input.instance = {
            questionCode: this.question.questionCode,
            questionnaireId: this.questionnaireId,
            value: {
                value1: '',
                value2: '',
                value3: '',
            },
            unit:
                this.question.value.unitValue1 ??
                this.question.value.unitValue2 ??
                this.question.value.unitValue3 ??
                '',
        };
        input.possibleUnits = undefined;
        input.instanceId = undefined;
        input.hidden = true;
        input.editForm.markAsPristine();
        this.cdr.detectChanges();
    }

    private AddQuestionInstanceInputEntry(questionInstance?: QuestionInstanceDto, hasToRefresh?: boolean): void {
        const value1Control = this._fb.control(null);
        const value2Control = this._fb.control(null);
        const value3Control = this._fb.control(null);
        const unitControl = this._fb.control(null);

        value1Control.setValue(questionInstance?.value.value1 ?? null);
        value2Control.setValue(questionInstance?.value.value2 ?? null);
        value3Control.setValue(questionInstance?.value.value3 ?? null);
        unitControl.setValue(questionInstance?.unit ?? null);

        const editForm = this._fb.group({
            value1Control: value1Control,
            value2Control: value2Control,
            value3Control: value3Control,
            unitControl: unitControl,
        });

        editForm.markAsPristine();
        const questionInstancesInputEntry: QuestionInstanceInputEntry = {
            value1ControlInitValue: questionInstance?.value.value1 ?? '',
            value2ControlInitValue: questionInstance?.value.value2 ?? '',
            value3ControlInitValue: questionInstance?.value.value3 ?? '',
            unitControlInitValue: questionInstance?.unit ?? '',
            value1Control: value1Control,
            value2Control: value2Control,
            value3Control: value3Control,
            unitControl: unitControl,
            editForm: editForm,
            instanceId: questionInstance?.id ?? undefined,
            instance: questionInstance ?? {
                questionCode: this.question.questionCode,
                questionnaireId: this.questionnaireId,
                value: {
                    value1: '',
                    value2: '',
                    value3: '',
                },
                unit: '',
            },
            isDisabledLike: this.isLocked,
            isPrefilled: questionInstance?.preFilled,
        };

        if (questionInstance && questionInstance.value.value1) {
            const enumEntry = this.domain1Map.get(questionInstance.value.value1);
            questionInstancesInputEntry.possibleUnits = enumEntry?.possibleUnits;
            if (questionInstance.unit && !enumEntry?.possibleUnits.includes(questionInstance.unit)) {
                questionInstancesInputEntry.possibleUnits?.push(questionInstance.unit);
            }
        }
        this.questionInstancesInputEntries.push(questionInstancesInputEntry);
        if (hasToRefresh) {
            this.hasNoNewEntry = false;
        }
    }

    public insertEntry() {
        // Locked scan questionnaires are read-only — no new entries.
        if (this.isLocked) {
            return;
        }
        const shownInputCandidate = this.questionInstancesInputEntries.find((i) => !i.instanceId && i.hidden);
        if (shownInputCandidate) {
            shownInputCandidate.hidden = false;
            this.nonHiddenEntries = [...this.nonHiddenEntries, shownInputCandidate];
            this.ngZone.run(() => {
                this.activeIndex = this.nonHiddenEntries.length - 1;
            });
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    onEnumChange(inputEntry: QuestionInstanceInputEntry): void {
        const value1 = inputEntry.value1Control.value as string | null;
        if (value1) {
            const enumEntry = this.domain1Map.get(value1);
            inputEntry.possibleUnits = enumEntry?.possibleUnits;
            inputEntry.unitControl.setValue(enumEntry?.possibleUnits ? enumEntry.possibleUnits[0] : null);
            if (enumEntry?.defaultValue && (!inputEntry.value2Control.value || !inputEntry.instanceId)) {
                inputEntry.value2Control.setValue(enumEntry.defaultValue);
            }
        } else {
            inputEntry.unitFromSelectedDropdown = '';
        }
        this.cdr.detectChanges();
    }

    private updateDomain(): void {
        if (this.question.value.value1Type === 'enum') {
            this.questionInstancesInputEntries.forEach((ie) => {
                const value1 = ie.value1Control.value as string | null;
                if (value1 !== null && this.domain1Map.has(value1)) {
                    const index = this.domain1.findIndex((e) => e.value === value1);
                    if (index !== -1) {
                        this.domain1.splice(index, 1);
                        this.sortOptions();
                    }
                }
            });
            this.cdr.detectChanges();
        }
    }

    private restoreDomainEntry(input: QuestionInstanceInputEntry, oldValue?: boolean): void {
        const lookupKey = oldValue
            ? input.value1ControlInitValue
            : ((input.value1Control.value as string | null) ?? '');
        const entry = this.domain1Map.get(lookupKey);
        if (entry) {
            this.domain1.splice(0, 0, entry);
            this.sortOptions();
        }
        this.cdr.detectChanges();
    }

    onInputEscape(inputMessage: inputUpdateMessage) {
        if (this.isLocked) {
            return;
        }
        const inputEntry = inputMessage.inputEntry;
        const questionField = inputMessage.questionField;
        const v1 = inputEntry.value1Control.value as string | null;
        const v2 = inputEntry.value2Control.value as string | null;
        const v3 = inputEntry.value3Control.value as string | null;
        const unitValue = inputEntry.unitControl.value as string | null;
        const toEffectiveValue = (raw: string | null): string | undefined =>
            raw === '' || raw === null ? undefined : raw;
        const callCompletion: Subject<QuestionInstanceDto | undefined> = new Subject<QuestionInstanceDto | undefined>();
        let instance: QuestionInstanceDto;
        if (
            (inputEntry.instanceId &&
                ((v1?.toString() !== inputEntry.value1ControlInitValue && questionField === 1) ||
                    (v2?.toString() !== inputEntry.value2ControlInitValue && questionField === 2) ||
                    (v3?.toString() !== inputEntry.value3ControlInitValue && questionField === 3) ||
                    (unitValue?.toString() !== inputEntry.unitControlInitValue && questionField === 4))) ||
            (!inputEntry.instanceId &&
                ((this.question.isDynamic && v1 !== null) || !this.question.isDynamic) &&
                ((v1 !== null && v1.toString() !== inputEntry.value1ControlInitValue && questionField === 1) ||
                    (v2 !== null && v2.toString() !== inputEntry.value2ControlInitValue && questionField === 2) ||
                    (v3 !== null && v3.toString() !== inputEntry.value3ControlInitValue && questionField === 3)))
        ) {
            if (
                (inputEntry.instanceId && !this.question.value.value2Type && v1 === null) ||
                (this.question.value.value2Type && v1 === null && v2 === null && v3 === null)
            ) {
                this.deleteEntry(inputEntry);
                return;
            }
            const resolvedUnit =
                inputEntry.possibleUnits && inputEntry.possibleUnits.length > 1
                    ? (unitValue ?? undefined)
                    : inputEntry.possibleUnits
                      ? inputEntry.possibleUnits[0]
                      : (this.question.value.unitValue1 ??
                        this.question.value.unitValue2 ??
                        this.question.value.unitValue3);
            if (!inputEntry.instanceId) {
                instance = {
                    questionCode: this.question.questionCode,
                    questionnaireId: this.questionnaireId,
                    value: {
                        value1: toEffectiveValue(v1),
                        value2: toEffectiveValue(v2),
                        value3: toEffectiveValue(v3),
                    },
                    unit: resolvedUnit,
                };
                inputEntry.instance = instance;
            } else {
                instance = inputEntry.instance;
                instance.value.value1 = toEffectiveValue(v1);
                instance.value.value2 = toEffectiveValue(v2);
                instance.value.value3 = toEffectiveValue(v3);
                instance.unit = resolvedUnit;
            }
            if (this.question.value.value1Type === 'enumRadio') {
                inputEntry.editForm.disable();
            }
            inputEntry.isDisabledLike = true;

            this.updateDomain();
            this.cdr.detectChanges();
            callCompletion.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
                next: (qqi) => {
                    if (qqi) {
                        this.postHogService.captureQuestionnaireEvent('questionnaire_answer_saved', {
                            questionnaire_type: 'scan',
                            question_code: this.question.questionCode,
                        });
                        if (inputEntry.instanceId) {
                            this.restoreDomainEntry(inputEntry, true);
                        }
                        this.updateInputEntry(qqi, inputEntry);
                    } else {
                        // A refused answer is otherwise invisible: the fields roll
                        // back, the client sees a red flash, and nothing is
                        // captured — indistinguishable from never having tried.
                        // The state action discards the error, so this reports
                        // that the server refused, not why; enough to rank the
                        // questions that fail most, which is where a badly worded
                        // label or a mis-designed field shows up.
                        this.postHogService.captureQuestionnaireEvent('questionnaire_answer_rejected', {
                            questionnaire_type: 'scan',
                            question_code: this.question.questionCode,
                            action: 'save',
                            question_field: questionField,
                            is_new: !inputEntry.instanceId,
                        });
                        this.restoreDomainEntry(inputEntry);
                        inputEntry.value1Control.setValue(inputEntry.value1ControlInitValue);
                        inputEntry.value2Control.setValue(inputEntry.value2ControlInitValue);
                        inputEntry.value3Control.setValue(inputEntry.value3ControlInitValue);
                        this.handleCompletionMessage(inputEntry, questionField);
                    }

                    inputEntry.isDisabledLike = this.isLocked;
                    this.hasNoNewEntry = true;
                    this.updateDomain();
                    this.cdr.detectChanges();
                    callCompletion.complete();

                    if (this.question.value.value1Type === 'enumRadio') {
                        inputEntry.editForm.enable();
                        this.cdr.detectChanges();
                    }
                },
            });

            if (this.parentQuestionInstance) {
                instance.relatedQuestionInstanceId = this.parentQuestionInstance.instanceId;
            }
            this.store.dispatch(
                new QuestionnaireAction.CreateUpdateAction(this.orgId, instance, questionField, callCompletion),
            );
        }
    }

    deleteEntry(inputEntry: QuestionInstanceInputEntry): void {
        if (this.isLocked) {
            return;
        }
        const callCompletion: Subject<QuestionInstanceDto[] | undefined> = new Subject<
            QuestionInstanceDto[] | undefined
        >();
        inputEntry.isDisabledLike = true;
        this.cdr.detectChanges();
        callCompletion.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
            next: (qqi) => {
                if (qqi) {
                    this.postHogService.captureQuestionnaireEvent('questionnaire_answer_deleted', {
                        questionnaire_type: 'scan',
                        question_code: this.question.questionCode,
                    });
                    if (this.question.isDynamic) {
                        this.restoreDomainEntry(inputEntry);
                    }
                    this.resetInputEntry(inputEntry);
                    this.sortOptions();
                    this.handleHiddenEntries();
                    if (this.subQuestion) {
                        const subQIToRemove = qqi.find((q) => q.questionCode === this.subQuestion?.questionCode);
                        if (subQIToRemove) {
                            this.removeChildComponent(subQIToRemove);
                        }
                    }
                    this.hasNoNewEntry = true;
                } else {
                    this.postHogService.captureQuestionnaireEvent('questionnaire_answer_rejected', {
                        questionnaire_type: 'scan',
                        question_code: this.question.questionCode,
                        action: 'delete',
                    });
                    this.handleCompletionMessage(inputEntry, this.question.value.value2Type ? 2 : 1);
                }
                inputEntry.isDisabledLike = this.isLocked;
                callCompletion.complete();
            },
        });
        this.store.dispatch(new QuestionnaireAction.DeleteAction(this.orgId, inputEntry.instance, callCompletion));
    }

    removeChildComponent(qI: QuestionInstanceDto): void {
        if (this.childComponent) {
            this.childComponent.removeChildEntry(qI);
        }
    }

    removeChildEntry(qI: QuestionInstanceDto): void {
        const inputEntry = this.questionInstancesInputEntries.find((i) => i.instanceId === qI.id);
        if (inputEntry) {
            inputEntry.value1Control.setValue(null);
            inputEntry.value2Control.setValue(null);
            inputEntry.value3Control.setValue(null);
        }
    }

    handleCompletionMessage(input: QuestionInstanceInputEntry, field: number) {
        if (field === 2) {
            input.input2UpdateFailed = true;
            setTimeout(() => {
                input.input2UpdateFailed = false;
                this.cdr.detectChanges();
            }, 1500);
        } else {
            input.input1UpdateFailed = true;
            setTimeout(() => {
                input.input1UpdateFailed = false;
                this.cdr.detectChanges();
            }, 1500);
        }
    }

    focusNextInput(inputId: string) {
        const currentIndex = this.extractIndexFromInputId(inputId);
        const currentQuestionCode = this.extractQuestionCode(inputId);
        const nextQuestionCode = currentQuestionCode + 1;
        const nextInput = this.findNextInput(currentIndex, currentQuestionCode, nextQuestionCode, inputId);

        if (nextInput) {
            this.blurCurrentInput(inputId);
            const inputElement = this.findInputElement(nextInput);
            if (inputElement) {
                inputElement.focus();
            }
        }
    }

    blurCurrentInput(inputId: string) {
        const currentInput = document.getElementById(inputId) as HTMLElement;
        currentInput.blur();
        if (currentInput.tagName === 'P-SELECT') {
            const dropDownWrapper = document.getElementsByClassName('p-select-list-container');
            for (let i = 0; i < dropDownWrapper.length; i++) {
                const element = dropDownWrapper[i] as HTMLElement;
                element.style.display = 'none';
            }
        }
    }

    extractIndexFromInputId(inputId: string): number {
        return parseInt(inputId.substring(inputId.lastIndexOf('_') + 1), 10);
    }

    extractQuestionCode(inputId: string): number {
        const questionCodeWIndex = inputId.substring(0, inputId.length - 2);
        const currentQuestionCode = inputId.startsWith('val_2_')
            ? +questionCodeWIndex.substring(7)
            : +questionCodeWIndex.substring(1);
        return currentQuestionCode;
    }

    findNextInput(
        currentIndex: number,
        currentQuestionCode: number,
        nextQuestionCode: number,
        inputId: string,
    ): HTMLElement | null {
        const nextQcodeToString = this.generateQuestionCodeString(nextQuestionCode);
        const currentQcodeToString = this.generateQuestionCodeString(currentQuestionCode);

        if (inputId.startsWith('val_2_')) {
            const nextInputEntry = this.questionInstancesInputEntries.find((_, i) => i === currentIndex + 1);
            return nextInputEntry?.hidden
                ? document.getElementById(`${nextQcodeToString}_0`)
                : document.getElementById(`${currentQcodeToString}_${currentIndex + 1}`) ||
                      document.getElementById(`${nextQcodeToString}_0`);
        } else {
            return (
                document.getElementById(`val_2_${currentQcodeToString}_${currentIndex}`) ||
                document.getElementById(`${currentQcodeToString}_${currentIndex + 1}`) ||
                document.getElementById(`${nextQcodeToString}_0`)
            );
        }
    }

    generateQuestionCodeString(questionCode: number): string {
        return `q${questionCode.toString().padStart(3, '0')}`;
    }

    findInputElement(nextInput: HTMLElement): HTMLElement | null {
        return (
            nextInput.querySelector('p-select') ||
            nextInput.querySelector('p-radiobutton') ||
            nextInput.querySelector('input')
        );
    }

    preloadTooltipContent() {
        const textKey = `question.${this.question.questionCode}.infoType.text`;
        const whereToFindKey = `question.${this.question.questionCode}.infoType.whereToFind`;
        const translatedText = this.translateService.keyExists(textKey)
            ? this.translateService.instant(textKey)
            : undefined;
        const whereToFind = this.translateService.keyExists(whereToFindKey)
            ? this.translateService.instant(whereToFindKey)
            : undefined;

        let tooltip = '';

        if (translatedText) {
            tooltip += `<p>${translatedText}</p>`;
        }

        if (translatedText && whereToFind) {
            tooltip += `<hr class="my-1">`;
        }

        if (whereToFind) {
            tooltip += `<p class="m-0">${whereToFind}</p>`;
        }

        this.tooltipContent = tooltip;
    }
}
